/**
 * ActivityTimeline — one bar per day of the observation window.
 *
 * The original demo drew all days in the same blue and listed the idle
 * stretches as chips underneath; in review nobody read the chips and the
 * gaps didn't register. So the emphasis is inverted here:
 *
 *   - idle runs are a solid red band, drawn behind the bars and readable as
 *     one shape rather than as N missing bars;
 *   - hovering anywhere inside a run highlights the WHOLE run and names its
 *     boundaries — "18–30 апреля · 13 дней без работы";
 *   - days with real work are muted, because "we did something" is the
 *     baseline expectation, not the finding.
 *
 * Bar height is sqrt-scaled: a 386-change bulk night would otherwise flatten
 * every hand-made edit into invisibility.
 *
 * No chart primitive in the design system (D6).
 *
 * @format
 */

import {useEffect, useMemo, useRef, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {RadiusS} from 'ui/tokens/ts/radii';

import {
  DAYS,
  EDITS,
  MEASURE_WIDE,
  OPERATIONS,
  dateRange,
  dayMonth,
  pluralize,
  toIsoDate,
  toLocalDate,
} from '../helpers';
import type {EditDay, Pause} from '../types';
import {DAY_OFF_LABEL, dayOff} from '../workCalendar';
import {PeriodAxis} from './PeriodAxis';
import {Tooltip} from './Tooltip';

export interface ActivityTimelineProps {
  days: EditDay[];
  idlePeriods: Pause[];
}

const CHART_HEIGHT = 220;

/**
 * Зазор между днями. Волосяной — он разделяет, а не разгораживает.
 */
const DAY_GAP = spacingPx('tight');

/**
 * Уже двух пикселей колонка дня перестаёт быть колонкой. Ниже этого порога
 * раскладку отдаём флексу, а лишнюю ширину — горизонтальной прокрутке.
 */
const MIN_DAY = 2;

/** Ширины колонок в целых пикселях и подпиксельная поправка ряда. */
interface DayColumns {
  widths: number[];
  /**
   * Сдвиг, которым левый край ряда сажается на целый пиксель.
   *
   * Целых ширин мало: если сам ряд начинается на половине пикселя, каждый
   * шов тоже встанет на половину, и мы вернёмся к тому, от чего уходили.
   * Полоса документа центрируется автополями и на нечётной ширине окна
   * получает ровно такую половину.
   */
  offset: number;
}

/**
 * Раскладывает дни по целым пикселям.
 *
 * Флекс делит остаток поровну и выдаёт дробные колонки: 6,578 px на день.
 * Края при этом ползут по долям пикселя, и зазор в один пиксель почти нигде
 * не попадает в целый физический — браузер размазывает его между двумя, шов
 * бледнеет и пропадает. На днях без заливки это незаметно, а подряд идущие
 * дни простоя сливаются в одну широкую палку.
 *
 * Поэтому ширины считаем сами: целая база каждому дню, остаток раздаём по
 * пикселю первым колонкам. Разница в пиксель при ширине колонки в семь глазу
 * недоступна, а швы после этого чёткие при любой плотности экрана.
 */
function useDayColumns(
  ref: React.RefObject<HTMLElement | null>,
  count: number,
): DayColumns | null {
  const [columns, setColumns] = useState<DayColumns | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || count === 0) return;

    const measure = () => {
      const box = node.getBoundingClientRect();
      const total = Math.floor(box.width);
      const available = total - DAY_GAP * (count - 1);

      // Места меньше, чем на колонки минимальной ширины: считать нечего,
      // пусть работают флекс и прокрутка.
      if (available < count * MIN_DAY) {
        setColumns(prev => (prev === null ? prev : null));
        return;
      }

      const base = Math.floor(available / count);
      const extra = available - base * count;
      const widths = Array.from({length: count}, (_, index) =>
        index < extra ? base + 1 : base,
      );
      // Поправку, уже стоящую на ряду, вычитаем — иначе следующий замер
      // считал бы её от собственного результата. Берём из стиля, а не из
      // памяти: замер зовут двое (наблюдатель за размером и событие окна),
      // и повторный вызов обязан давать тот же ответ.
      const shifted = parseFloat(getComputedStyle(node).left) || 0;
      const offset = -((box.left - shifted) % 1);

      setColumns(prev =>
        prev !== null &&
        prev.offset === offset &&
        prev.widths.length === widths.length &&
        prev.widths.every((value, index) => value === widths[index])
          ? prev
          : {widths, offset},
      );
    };

    measure();

    // Наблюдатель за размером ловит смену ширины, но не смену положения.
    // Полоса документа упирается в свой максимум и дальше только едет от
    // центрирования: окно шире на пиксель — ряд сдвинулся на полпикселя, а
    // ширина та же, и наблюдатель молчит. Поэтому слушаем ещё и окно.
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, count]);

  return columns;
}

/**
 * 128 day-columns need roughly 480px to stay legible. Below that the chart
 * scrolls inside its own rail rather than widening the page — a report that
 * scrolls sideways as a whole is unreadable on a phone.
 */
const ScrollArea = styled(Box)({
  overflowX: 'auto',
  overflowY: 'hidden',
  maxWidth: '100%',
});

const ScrollInner = styled(Box)({
  minWidth: 480,
});

// Box rather than Row: the chart needs a mouse handler, and the layout
// primitives deliberately expose no DOM props.
const Chart = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-end',
  height: CHART_HEIGHT,
  // Подпиксельная поправка ставится через `left`, а не через поле: смещение
  // относительно позиционированного элемента не трогает раскладку, поэтому
  // ширина ряда не меняется и наблюдатель за размером не зацикливается.
  position: 'relative',
  // Hairline between day columns — the tightest spacing token there is.
  gap: DAY_GAP,
  paddingBottom: spacingPx('xxs'),
  borderBottom: `1px solid ${surfaceVar('surface3')}`,
});

/** Где начинается разогрев: до этой высоты цвет колонки ровно прежний. */
const HEAT_START = 60;
/** Столько промежуточных остановок кладём на кривую. */
const HEAT_STEPS = 8;

/** Плавный старт и плавный финиш: 3t² − 2t³. */
const smoothstep = (t: number) => t * t * (3 - 2 * t);

/**
 * Заливка колонки простоя: ровный цвет сверху и разогрев к основанию.
 *
 * Градиент идёт не от края, а с 60 % высоты — иначе колонка читается как
 * двухцветная лента. Но линейная растяжка от этой отметки давала излом:
 * до неё цвет стоит, после начинает меняться с полной скоростью, и глаз
 * ловит границу как ребро.
 *
 * Поэтому цвет считается по `smoothstep` и раскладывается остановками. У
 * этой кривой производная на обоих концах нулевая — переход трогается с
 * места и останавливается постепенно, и ни начала, ни конца не видно.
 */
const idleFill = (base: string, bright: string) => {
  const stops = [`${base} 0%`, `${base} ${HEAT_START}%`];
  for (let step = 1; step <= HEAT_STEPS; step += 1) {
    const t = step / HEAT_STEPS;
    const share = (smoothstep(t) * 100).toFixed(1);
    const at = (HEAT_START + (100 - HEAT_START) * t).toFixed(1);
    stops.push(`color-mix(in srgb, ${bright} ${share}%, ${base}) ${at}%`);
  }
  return `linear-gradient(180deg, ${stops.join(', ')})`;
};

/**
 * Тусклость подложки нерабочего дня.
 *
 * Восемь процентов: подложка обязана читаться как «этот день другой», но
 * оставаться фоном. Прежние сплошные `surface2` давали серое пятно почти
 * той же светлоты, что и полоса автоматики, и день без работ отличить от
 * дня с ночными операциями было нельзя.
 */
const DAY_OFF_FILL = `color-mix(in srgb, ${surfaceVar('onSurfaceMuted')} 8%, transparent)`;

const Day = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$idle: boolean; $highlighted: boolean; $quiet: boolean; $off: boolean}>(({
  theme,
  $idle,
  $highlighted,
  $quiet,
  $off,
}) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  position: 'relative',
  // Отклик на курсор — подъём на полтона, а не смена цвета. Диапазон
  // простоя и так уже красный: ему нужно показать, что он выделен, а не
  // что он стал важнее.
  // Нерабочий день помечен подложкой — но только когда он не входит в
  // простой: красная полоса и так означает «не работали», и вторая
  // заливка поверх неё сказала бы то же самое дважды.
  //
  // Ветка живёт внутри одного `background`, а не отдельным
  // `backgroundColor` рядом: сокращённое свойство сбрасывает цвет фона, и
  // объявленный выше он был бы стёрт этой же строкой.
  background: $idle
    ? $highlighted
      ? idleFill(
          `color-mix(in srgb, ${theme.palette.bad.hover} 45%, ${theme.palette.bad.container})`,
          theme.palette.bad.hover,
        )
      : idleFill(theme.palette.bad.container, theme.palette.bad.main)
    : $off
      ? DAY_OFF_FILL
      : 'transparent',
  transition: 'background-color 120ms ease',
  // Любой день отзывается на курсор, а не только красный: иначе непонятно,
  // что подсказка вообще есть.
  //
  // Отдельный день без работ (`$quiet`) отзывается краснотой, а не серым.
  // Заливку ему не даём: красная полоса на графике означает «долго не
  // работали», и одиночный день, покрашенный тем же цветом, обесценил бы
  // это различие. Но и нейтральный отклик врал — день-то пустой.
  '&:hover': {
    ...($idle
      ? null
      : {
          backgroundColor: $quiet
            ? `color-mix(in srgb, ${theme.palette.bad.container} 60%, transparent)`
            : surfaceVar('surface2'),
        }),
  },
  // Размытого свечения у основания простоя здесь нет и не будет. Оно
  // требовало собственного слоя поверх графика, а график — плотная сетка
  // из 128 колонок: свет либо накрывал соседние столбики, либо уходил под
  // них и пропадал. Разогрев внизу самой колонки говорит то же самое и
  // ничего не перекрывает.
}));

/**
 * Колонка дня целиком служит триггером подсказки. `Tooltip` оборачивает её
 * своим инлайн-якорем, поэтому геометрию флекс-ячейки задаём здесь.
 */
const DayAnchor = styled(Tooltip, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$cell?: number}>(({$cell}) => ({
  // Пока ширина не измерена — обычный флекс: документ должен выглядеть
  // целым и до первого замера.
  flex: $cell === undefined ? '1 1 0' : 'none',
  width: $cell,
  minWidth: MIN_DAY,
  alignSelf: 'stretch',
}));

const Bar = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$kind: EditDay['kind']; $height: number}>(({theme, $kind, $height}) => ({
  width: '100%',
  height: $height,
  borderRadius: `${RadiusS} ${RadiusS} 0 0`,
  backgroundColor: $kind === 'MANUAL' ? theme.palette.primary.main : surfaceVar('onSurfaceMuted'),
  opacity: $kind === 'MANUAL' ? 0.85 : 0.35,
}));

const LegendRow = styled(Row)({
  ...text('body.l'),
  flex: '1 1 220px',
  minWidth: 200,
  alignItems: 'flex-start',
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE_WIDE,
});

const Swatch = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$color: string; $opacity?: number}>(({$color, $opacity = 1}) => ({
  width: 12,
  height: 12,
  borderRadius: 3,
  backgroundColor: $color,
  opacity: $opacity,
  flex: 'none',
  marginTop: spacingPx('tight'),
}));

/**
 * Строка отклика держит своё место всегда: без наведения по центру стоит
 * приглашение, при наведении — границы простоя. Если бы она появлялась
 * только по наведению, график дёргался бы по высоте на каждом движении
 * мыши.
 */
const ReadoutRail = styled(Box)({
  position: 'relative',
  minHeight: 40,
});

const ReadoutText = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$active: boolean; $left: string}>(({theme, $active, $left}) => ({
  ...text('label.m'),
  position: 'absolute',
  top: 0,
  left: $left,
  transform: 'translateX(-50%)',
  // Подпись центрируется по своему простою, но у краёв это вынесло бы её
  // за страницу: позиция зажата в безопасную полосу, а сам текст при
  // нехватке места переносится, а не растягивает документ.
  maxWidth: 'min(100%, 420px)',
  textAlign: 'center',
  color: $active ? theme.palette.bad.main : surfaceVar('onSurfaceMuted'),
  transition: 'left 160ms ease, color 120ms ease',
}));

/**
 * Нерабочий день. Тем же токеном, что и подложка дня, но в полную силу:
 * подложка — он при восьми процентах, слово — он при ста.
 */
const TipOff = styled(Box)({
  ...text('label.m'),
  color: surfaceVar('onSurfaceMuted'),
});

/** Дата — следом за пометкой дня: она отвечает «где я сейчас». */
const TipDate = styled(Box)({
  ...text('label.m'),
  color: surfaceVar('onSurface'),
});

const TipWhat = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
});

/**
 * Дата и что в этот день происходило — двумя строками. Одной строкой это
 * читается как список через разделитель, а тут два разных факта: когда и
 * что.
 */
function DayInfo({day}: {day: EditDay}) {
  const when = dayMonth(day.date);

  const what =
    day.count === 0
      ? 'Работ не было'
      : day.kind === 'MANUAL'
        ? `${pluralize(day.count, EDITS)} руками`
        : `${pluralize(day.count, OPERATIONS)} автоматики`;

  const off = dayOff(day.date, day.dayType);

  return (
    <>
      {/* Нерабочий день — первой строкой: он объясняет всё остальное в
          подсказке. Цвет тот же, которым помечен сам день, только в полную
          силу — связь отметки и слова буквальная, а не «похожий серый». */}
      {off && <TipOff>{DAY_OFF_LABEL[off]}</TipOff>}
      <TipDate>{when}</TipDate>
      <TipWhat>{what}</TipWhat>
    </>
  );
}

export function ActivityTimeline({days, idlePeriods}: ActivityTimelineProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const dates = useMemo(() => days.map(d => d.date), [days]);

  // Колонки дня стоят на целых пикселях: иначе волосяной зазор между ними
  // размазывается между физическими пикселями и дни простоя слипаются.
  const chartRef = useRef<HTMLDivElement>(null);
  const columns = useDayColumns(chartRef, days.length);

  /** date → index in idlePeriods, so hovering one day lights the whole run. */
  const periodByDate = useMemo(() => {
    const map = new Map<string, number>();
    idlePeriods.forEach((period, index) => {
      // Календарные даты перебираем в локальной полуночи: `new Date(iso)`
      // читает строку как UTC, и `toISOString` вернул бы соседний день в
      // любой зоне, кроме нулевой.
      const cursor = toLocalDate(period.from);
      const end = toLocalDate(period.to);
      if (!cursor || !end) return;
      while (cursor <= end) {
        map.set(toIsoDate(cursor), index);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [idlePeriods]);

  const maxCount = useMemo(() => Math.max(...days.map(d => d.count), 1), [days]);
  const active = hovered !== null ? idlePeriods[hovered] : null;

  /**
   * Подпись встаёт над серединой того простоя, о котором говорит. Позиция
   * считается по доле дней: колонки равной ширины, поэтому середина
   * диапазона в днях — это и есть середина полосы на экране.
   */
  const readoutLeft = useMemo(() => {
    if (!active) return '50%';
    const first = days.findIndex(d => d.date === active.from);
    if (first < 0) return '50%';
    const middle = first + active.days / 2;
    const pct = (middle / days.length) * 100;
    // Зажимаем: у самого края translateX(-50%) утащил бы половину подписи
    // за пределы страницы.
    return `${Math.min(80, Math.max(20, pct))}%`;
  }, [active, days]);

  return (
    <Stack gap='s'>
      {/* Легенда — три предложения, а не подписи к образцам цвета:
          читателю нужно понять, что означает день, а не выучить палитру. */}
      <Row gap='m' wrap='wrap' alignY='start'>
        <LegendRow gap='s' alignY='start'>
          <Swatch $color='var(--ads-palette-primary-main)' $opacity={0.85} />
          Дни, когда специалисты вручную вносили исправления
        </LegendRow>
        <LegendRow gap='s' alignY='start'>
          <Swatch $color={surfaceVar('onSurfaceMuted')} $opacity={0.35} />
          Дни, когда происходили массовые автоматические операции
        </LegendRow>
        {/* Выходных в легенде нет намеренно: суббота под бледной подложкой
            опознаётся без объяснений, а строка в подсказке называет день
            прямо. Место в легенде стоит дорого — оно тратится на то, чего
            из графика самому не понять.

            Красное на графике означает не «день без работ», а затяжной
            простой: одиночные пустые дни в полосу не собираются и заливки
            не получают. Прежняя подпись обещала красноту там, где её нет, —
            на кабинете с двумя пустыми днями подряд легенда указывала на
            цвет, которого на графике не было ни разу. */}
        <LegendRow gap='s' alignY='start'>
          <Swatch $color='var(--ads-palette-bad-container)' />
          Красным — отрезки, когда над рекламой долго не работали
        </LegendRow>
      </Row>

      <ScrollArea>
        <ScrollInner>
          <Chart
            ref={chartRef}
            style={{left: columns?.offset ?? 0}}
            onMouseLeave={() => setHovered(null)}
          >
            {days.map((day, index) => {
              const periodIndex = periodByDate.get(day.date);
              const isIdle = periodIndex !== undefined;
              const height =
                day.count > 0 ? Math.max(3, Math.round(Math.sqrt(day.count / maxCount) * 130)) : 0;
              return (
                <DayAnchor
                  key={day.date}
                  content={<DayInfo day={day} />}
                  $cell={columns?.widths[index]}
                  width={220}
                  followCursor
                  bare
                >
                  <Day
                    $idle={isIdle}
                    $quiet={!isIdle && day.count === 0}
                    $off={Boolean(dayOff(day.date, day.dayType))}
                    $highlighted={isIdle && hovered === periodIndex}
                    onMouseEnter={() => setHovered(periodIndex ?? null)}
                  >
                    {height > 0 && <Bar $kind={day.kind} $height={height} />}
                  </Day>
                </DayAnchor>
              );
            })}
          </Chart>

          {/* Inside the same scroller so the axis stays under its days. */}
          <PeriodAxis dates={dates} />
        </ScrollInner>
      </ScrollArea>

      <ReadoutRail>
        <ReadoutText $active={Boolean(active)} $left={readoutLeft}>
          {active ? (
            `${dateRange(active.from, active.to)} — ${pluralize(active.days, DAYS)} без работы`
          ) : idlePeriods.length > 0 ? (
            // Перенос назначен после запятой, а не отдан браузеру: сам он
            // рвал строку перед последним словом и оставлял его висеть
            // одно. Запятая — единственное место, где разрыв осмыслен.
            <>
              Наведите на красную область,
              <br />
              чтобы увидеть границы простоя
            </>
          ) : (
            // Простоев нет — звать наводить на красное некуда: красного на
            // графике в этом случае не бывает вовсе.
            <>
              Затяжных простоев за период не было.
              <br />
              Наведите на день, чтобы увидеть, что в нём происходило
            </>
          )}
        </ReadoutText>
      </ReadoutRail>
    </Stack>
  );
}
